import React from 'react';
import { ArrowDownIcon, ArrowLeftRightIcon, ArrowRightIcon } from 'lucide-react';
import { IMAGES } from '../data/site';
import { QuoteForm } from './QuoteForm';
export type HeroVariant = 'van' | 'storefront';
const HERO_ALT = 'Concept image of two Kosin’s Glass service vans at a commercial building, one with its cargo door open';
const STOREFRONT_ALT = 'The Kosin’s Glass brick storefront with its tall freestanding red sign and the company’s van and service trucks parked in front';

/* Neutral black scrim for the brighter storefront photo: strongest behind the
   copy, easing off smoothly over the lower vehicle and road area. */
const STOREFRONT_SCRIM: React.CSSProperties = {
  backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,0.76) 0%, rgba(0,0,0,0.74) 40%, rgba(0,0,0,0.60) 62%, rgba(0,0,0,0.42) 82%, rgba(0,0,0,0.30) 100%), linear-gradient(90deg, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.08) 45%, rgba(0,0,0,0) 75%)'
};
const PHOTO_CLASS = 'absolute inset-x-0 top-0 h-[600px] w-full sm:h-[720px] lg:h-[620px] xl:h-full';
function buildCompareHref(variant: HeroVariant): string {
  if (typeof window === 'undefined') {
    return variant === 'storefront' ? '/' : '/?hero=storefront';
  }
  const params = new URLSearchParams(window.location.search);
  if (variant === 'storefront') {
    params.delete('hero');
  } else {
    params.set('hero', 'storefront');
  }
  const query = params.toString();
  return `${window.location.pathname}${query ? `?${query}` : ''}`;
}
interface HeroProps {
  variant?: HeroVariant;
}
export function Hero({
  variant = 'van'
}: HeroProps) {
  const isStorefront = variant === 'storefront';
  const compareHref = buildCompareHref(variant);
  const compareLabel = isStorefront ? 'View Van Photo Version' : 'View Storefront Photo Version';
  return <section aria-labelledby="hero-heading" className="relative isolate w-full bg-kosin-mist xl:bg-kosin-charcoal" data-id="element-146" data-mp="2hinc2hdd4iky">
      {isStorefront ? <img src={IMAGES.heroStorefront} alt={STOREFRONT_ALT} width={1672} height={941} className={`${PHOTO_CLASS} object-cover object-center [filter:saturate(0.72)]`} data-id="element-147" data-mp="2lmuyg7wclmg0" /> : <picture data-id="element-148" data-mp="ibowkga76es9">
          <source media="(min-width: 1024px)" srcSet={IMAGES.heroWide} width={2172} height={724} data-id="element-149" data-mp="id9ri70fbzqn" />
          <img src={IMAGES.heroMobile} alt={HERO_ALT} width={1086} height={1448} className={`${PHOTO_CLASS} object-cover object-bottom lg:object-left-bottom`} data-id="element-150" data-mp="1b0vupe5qp2cz" />
        </picture>}

      {isStorefront ? <div aria-hidden="true" className={PHOTO_CLASS} style={STOREFRONT_SCRIM} data-id="element-151" data-mp="114ssy9esv5qk" /> : <div aria-hidden="true" className="absolute inset-x-0 top-0 h-[335px] bg-black/65 sm:h-[300px] lg:hidden" data-id="element-152" data-mp="x37am8jhxz8z" />}

      <div className="relative mx-auto max-w-shell px-[18px] sm:px-6 xl:grid xl:min-h-[620px] xl:grid-cols-[minmax(0,1fr)_400px] xl:items-start xl:gap-9 xl:py-6" data-id="element-153" data-mp="3hr3q7rbx233p">
        <div className="min-h-[600px] pt-6 sm:min-h-[720px] sm:pt-8 lg:min-h-[620px] xl:min-h-0 xl:pt-0" data-id="element-154" data-mp="21awca5mhyleu">
          <p className={`flex items-center gap-3 text-[16px] font-bold ${isStorefront ? 'text-white' : 'text-kosin-ondark'}`} data-id="element-155" data-mp="26a99nkazvavo">
            <span className="h-[3px] w-8 shrink-0 bg-kosin-ondark" aria-hidden="true" data-id="element-156" data-mp="2b2k9yo9ncyor" />
            Kosin’s Glass
          </p>

          <h1 id="hero-heading" className={`mt-2 max-w-[700px] text-[30px] font-bold leading-[1.1] text-white sm:text-[38px] xl:text-[44px] ${isStorefront ? '[text-shadow:0_1px_6px_rgba(0,0,0,0.45)]' : '[text-shadow:0_2px_4px_rgba(0,0,0,0.9)]'}`} data-id="element-157" data-mp="2kxc6rpvenhih">
            Glass Repair, Replacement &amp; Custom Glass
          </h1>

          <p className="mt-3 max-w-[560px] text-[16px] leading-[1.5] text-white sm:text-[17px] [text-shadow:0_1px_12px_rgba(0,0,0,0.6)]" data-id="element-158" data-mp="zlsw3oz2lx81">
            Family-owned since 2000, serving Southeast Michigan’s vehicles,
            homes and businesses.
          </p>

          <p className="mt-3 text-[14px] leading-5 text-white sm:text-[15px] [text-shadow:0_1px_12px_rgba(0,0,0,0.6)]" data-id="element-159" data-mp="e8imhpn92na6">
            <span className="block font-semibold sm:inline" data-id="element-160" data-mp="9b0cdvxhy20l">Three Convenient Locations</span>
            <span className="mx-2 hidden text-white/60 sm:inline" aria-hidden="true" data-id="element-161" data-mp="3hr26y5zosouf">
              |
            </span>
            Howell • Ann Arbor • Novi
          </p>

          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-[14px] font-semibold text-white" data-id="element-162" data-mp="3rgby9jmpi3hh">
            <a href="#hero-quote" className="focus-light inline-flex min-h-8 items-center gap-2 underline underline-offset-4 xl:hidden" data-id="element-163" data-mp="3mp3migxwm615">
              Get A Free Quote
              <ArrowDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-164" data-mp="2tzn1438pbpef" />
            </a>
            <a href="#services" className="focus-light inline-flex min-h-8 items-center gap-2 underline underline-offset-4" data-id="element-165" data-mp="19ids8cweehte">
              Explore Our Services
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-166" data-mp="zl95aauqaibj" />
            </a>
          </div>
        </div>

        <div id="hero-quote" className="pb-8 pt-5 xl:w-[400px] xl:justify-self-end xl:py-0" data-id="element-167" data-mp="14kcabvapkoji">
          <QuoteForm idPrefix="hero" data-id="element-168" data-mp="1sa6nyf2uqh4t" />
        </div>

        <a href={compareHref} className="focus-light absolute left-[18px] top-[544px] z-10 inline-flex items-center gap-2 rounded bg-black/60 px-3 py-1.5 text-[13px] font-semibold text-white underline underline-offset-4 hover:bg-black/75 transition-colors duration-150 ease-kosin sm:left-6 sm:top-[664px] lg:top-[564px] xl:bottom-5 xl:top-auto" data-id="element-169" data-mp="1s7by2812k440">
          <ArrowLeftRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-170" data-mp="7s0c11ik6bdb" />
          {compareLabel}
        </a>
      </div>
    </section>;
}